import { BookOpen, ListNumbers } from "@phosphor-icons/react"
import { CheckCircle, Atom, Flask, Calculator } from "@phosphor-icons/react"
import { useState, useEffect, useContext } from "react"
import StatCell from "../generic/StatCell"
import { QSTATUS } from "../../../../../apps/frontend/src/utils/strings"
import TableLoader from "../generic/TableLoader"
import NoData from "../generic/NoData"
import TableHeader from "../../components/generic/TableHeader"
import { TabsContainer, Tab } from "../../utils/SubjectTabs"
import { ThemeContext } from "../../../../../apps/frontend/src/context/ThemeStore"

const QsTypeBreakupSummary = ({ data, loading, onResetFilters }) => {
  const [activeTab, setActiveTab] = useState(null)
  const [showPercentage, setShowPercentage] = useState(true)
  const [summaryHeaders, setSummaryHeaders] = useState([])

  // ✅ Get dark/light mode info from ThemeContext
  const { isDarkTheme } = useContext(ThemeContext)

  console.log("Rendering QsTypeBreakupSummary with props:", {
    data,
    loading,
    onResetFilters,
  })

  useEffect(() => {
    if (data?.summary?.[0]) {
      const summaryTabNames = Object.keys(data.summary[0])
      const newObjects = summaryTabNames
        .filter(tab => tab !== "subjectMeta")
        .map((tab, index) => ({
          id: index,
          label: tab,
          shortName: tab,
          title: data.summary[0][tab]?.questionTypeMeta?.questionTypeTitle,
          icon: data.summary[0][tab]?.questionTypeMeta?.lightThemeIcon,
          darkIcon: data.summary[0][tab]?.questionTypeMeta?.darkThemeIcon,
          position: data.summary[0][tab]?.questionTypeMeta?.position
        }))
      
      // Sort by position if available
      const sortedHeaders = newObjects.sort((a, b) => (a.position || 0) - (b.position || 0))
      setSummaryHeaders(sortedHeaders)
      setActiveTab(sortedHeaders[0]?.shortName)
    }
  }, [data])

  if (!data?.summary) return null

  console.log("data in QsTypeBreakupSummary:", data)

return (
  <div>
    <TableHeader headerTitle="Summary" showBottomBorder={false} />

    {/* Main container with borders */}
    <div className="border-l border-r border-b border-[var(--q3-stroke-light)] rounded-b-lg overflow-hidden">

      {/* Tabs with independent horizontal scroll */}
      <div className="border-b border-[var(--q3-stroke-light)]">
        <div className="w-full overflow-x-auto whitespace-nowrap no-scrollbar">
          <TabsContainer color="var(--q3-neutral-default)" className="flex">
            {summaryHeaders.map((tab) => (
              <Tab
                key={tab.id}
                isActive={activeTab === tab.shortName}
                color={
                  activeTab === tab.shortName
                    ? "var(--q3-neutral-default)"
                    : "var(--q3-neutral-light)"
                }
                tabColor="var(--q3-neutral-default)"
                bgColor="var(--q3-surface-dim)"
                onClick={() => setActiveTab(tab.shortName)}
                className="flex-shrink-0"
              >
                {(isDarkTheme ? tab.darkIcon : tab.icon) && (
                  <img
                    src={isDarkTheme ? tab.darkIcon : tab.icon}
                    alt={tab.title}
                    className="w-4 h-4"
                  />
                )}
                <p className="mb-0 text-sm font-medium">{tab.title}</p>
              </Tab>
            ))}
          </TabsContainer>
        </div>
      </div>

     
      <div className="overflow-x-auto">
        <table className="min-w-[700px] w-full border-collapse">
          <thead>
            <tr className="bg-[var(--q3-surface-dim)] border-b border-[var(--q3-stroke-light)] text-sm font-medium text-[var(--q3-neutral-default)]">
              <th className="border-r border-[var(--q3-stroke-light)] min-w-[300px] p-4 text-left">
                Subject
              </th>
              <th className="border-r border-[var(--q3-stroke-light)] p-4 text-center text-[var(--q3-green-normal)]">
                Attempted Correct
              </th>
              <th className="border-r border-[var(--q3-stroke-light)] p-4 text-center text-[var(--q3-danger-normal)]">
                Attempted Wrong
              </th>
              <th className="border-r border-[var(--q3-stroke-light)] p-4 text-center text-[var(--q3-indigo-normal)]">
                Not Attempted
              </th>
              <th className="p-4 text-center text-[var(--q3-neutral-light)]">
                Not Visited Qs
              </th>
            </tr>
          </thead>
          <tbody>
            <TableLoader loading={loading}>
              {data?.summary?.length === 0 ? (
                <tr>
                  <td colSpan="5">
                    <NoData onResetAll={onResetFilters} />
                  </td>
                </tr>
              ) : (
                data?.summary?.map((subject, index) => {
                  const subjectMeta = subject.subjectMeta;
                  return (
                    <tr
                      key={index}
                      className="border-t border-[var(--q3-stroke-light)] text-sm text-[var(--q3-neutral-default)]"
                    >
                      {/* Subject Name */}
                      <td className="border-r border-[var(--q3-stroke-light)] min-w-[300px] p-4">
                        <div className="flex items-center gap-2">
                          <img
                            src={subjectMeta?.icon}
                            alt={subjectMeta?.title}
                            className="w-5 h-5 rounded-full"
                          />
                          <span className="font-medium text-[var(--q3-neutral-default)]">
                            {subjectMeta?.title}
                          </span>
                        </div>
                      </td>

                      {/* Correct */}
                      <td className="border-r border-[var(--q3-stroke-light)] text-center text-green-600">
                        <StatCell
                          top={subject[activeTab]?.correct}
                          bottom={subject[activeTab]?.totalQuestions}
                          showPercentage={showPercentage}
                          {...QSTATUS.CORRECT}
                        />
                      </td>

                      {/* Incorrect */}
                      <td className="border-r border-[var(--q3-stroke-light)] text-center text-red-600">
                        <StatCell
                          top={subject[activeTab]?.incorrect}
                          bottom={subject[activeTab]?.totalQuestions}
                          showPercentage={showPercentage}
                          {...QSTATUS.INCORRECT}
                        />
                      </td>

                      {/* Not Attempted */}
                      <td className="border-r border-[var(--q3-stroke-light)] text-center text-blue-600">
                        <StatCell
                          top={subject[activeTab]?.unanswered}
                          bottom={subject[activeTab]?.totalQuestions}
                          showPercentage={showPercentage}
                          {...QSTATUS.NOT_ATTEMPTED}
                        />
                      </td>

                      {/* Not Visited */}
                      <td className="text-center text-gray-600">
                        <StatCell
                          top={subject[activeTab]?.notvisited}
                          bottom={subject[activeTab]?.totalQuestions}
                          showPercentage={showPercentage}
                          {...QSTATUS.NOT_VISITED}
                          themedText={true}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </TableLoader>
          </tbody>
        </table>
      </div>
    </div>
  </div>
);


}

export default QsTypeBreakupSummary
